import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── Role-based menu config ─────────────────────────────────────────────────────
const MENU = {
  super_admin: [
    { icon: '📅', label: 'All Events',    path: '/admin/super' },
    { icon: '🏪', label: 'Dealers',       path: '/admin/super/dealers' },
    { icon: '📷', label: 'Photographers', path: '/admin/super/photographers' },
    { icon: '🎭', label: 'Event Owners',  path: '/admin/super/event-owners' },
    { icon: '👥', label: 'Visitors',      path: '/admin/super/visitors' },
    { icon: '📦', label: 'Packages',      path: '/admin/super/packages' },
    { icon: '📡', label: 'Live Status',   path: '/admin/super/live' },
    { icon: '🔍', label: 'Investigate',   path: '/admin/super/investigate' },
  ],
  photographer: [
    { icon: '📅', label: 'My Events', path: '/admin/events' },
  ],
  event_owner: [
    { icon: '📅', label: 'My Events', path: '/admin/events' },
  ],
  dealer: [
    { icon: '📊', label: 'Dashboard', path: '/dealer/dashboard' },
  ],
  visitor: [],
};

const ROLE_COLORS = {
  super_admin:  { bg: '#7c3aed', text: '#fff', label: 'Super Admin' },
  photographer: { bg: '#0f3460', text: '#fff', label: 'Photographer' },
  dealer:       { bg: '#065f46', text: '#fff', label: 'Dealer' },
  event_owner:  { bg: '#92400e', text: '#fff', label: 'Event Owner' },
  visitor:      { bg: '#374151', text: '#fff', label: 'Visitor' },
};

const SIDEBAR_W = 220;
const HEADER_H  = 48;

const AppLayout = ({ children }) => {
  const { user, token, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [isMobile,    setIsMobile]    = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!token) { navigate('/login', { replace: true }); return; }
    const storedPwd = localStorage.getItem('ep_password');
    if (storedPwd === '1234' && location.pathname !== '/change-password') {
      navigate('/change-password', { replace: true });
    }
  }, [token, navigate, location.pathname]);

  if (!user) return null;

  const role      = user.role || 'visitor';
  const roleStyle = ROLE_COLORS[role] || ROLE_COLORS.visitor;
  const menuItems = MENU[role] || [];

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  const NavItem = ({ item }) => {
    const active = location.pathname === item.path ||
                   (item.path !== '/admin/super' && location.pathname.startsWith(item.path));
    return (
      <button
        onClick={() => { navigate(item.path); if (isMobile) setSidebarOpen(false); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 9,
          width: '100%', padding: '9px 14px',
          background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
          border: 'none', borderLeft: active ? '3px solid #fff' : '3px solid transparent',
          color: '#fff', fontSize: 13, fontWeight: active ? 700 : 400,
          cursor: 'pointer', textAlign: 'left', borderRadius: '0 6px 6px 0',
          transition: 'background 0.15s',
        }}
      >
        <span style={{ fontSize: 15 }}>{item.icon}</span>
        <span>{item.label}</span>
      </button>
    );
  };

  const displayName = user.full_name || user.name || '—';
  const displayId   = user.id;
  const displayMob  = user.mobile_number || user.MobileNumber || '—';

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* ── Top Header ─────────────────────────────────────────────────────── */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: HEADER_H, zIndex: 200,
        background: '#1a1a2e', color: '#fff',
        display: 'flex', alignItems: 'center', padding: '0 14px', gap: 10,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}>
        {/* Hamburger */}
        <button
          onClick={() => setSidebarOpen(o => !o)}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, padding: '2px 4px', lineHeight: 1, flexShrink: 0 }}
          title="Toggle sidebar"
        >☰</button>

        {/* Brand */}
        <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: '-0.02em', color: '#e0e7ff', whiteSpace: 'nowrap' }}>
          📷 Smart Click Memories
        </span>

        <div style={{ flex: 1 }} />

        {/* User info — compact single line */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <span style={{ color: '#e0e7ff', fontWeight: 700, whiteSpace: 'nowrap' }}>{displayName}</span>
          <span style={{ color: '#64748b' }}>·</span>
          <span style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>#{displayId} · {displayMob}</span>
          <span style={{
            background: roleStyle.bg, color: roleStyle.text,
            fontSize: 9, fontWeight: 700, padding: '2px 7px',
            borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
          }}>{roleStyle.label}</span>
          <button
            onClick={handleLogout}
            style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, color: '#fca5a5', fontSize: 11, fontWeight: 600, padding: '3px 9px', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >Logout</button>
        </div>
      </header>

      {/* ── Sidebar ──────────────────────────────────────────────────────────── */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 299 }} />
      )}

      <nav style={{
        position: 'fixed', top: HEADER_H, left: 0, bottom: 0,
        width: SIDEBAR_W,
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0f3460 100%)',
        color: '#fff', zIndex: 300,
        transform: sidebarOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_W}px)`,
        transition: 'transform 0.25s ease',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
      }}>
        {/* Menu items */}
        <div style={{ flex: 1, paddingTop: 8 }}>
          {menuItems.map(item => <NavItem key={item.path} item={item} />)}
        </div>
      </nav>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <main style={{
        paddingTop: HEADER_H,
        paddingLeft: (!isMobile && sidebarOpen) ? SIDEBAR_W : 0,
        transition: 'padding-left 0.25s ease',
        minHeight: '100vh',
      }}>
        {children}
      </main>

    </div>
  );
};

export default AppLayout;
